import React, { useEffect, useReducer, useState } from 'react';
import { graphql, navigate } from 'gatsby';
import { WindowLocation } from '@reach/router';
import { BLOG_CATEGORIES, BLOG_INDEX_PAGE_LIMIT } from '../../v2/constants';
import blogReducer from '../../v2/utils/reducers';
import { pageNumberController } from '../../v2/utils';
import blogPostsQuery from '../../v2/utils/queries';
import BlogPostList from '../../v2/components/blog/BlogPostList';
import { IBlogPosts } from '../../v2/interfaces/IBlogPosts';
import BlogLayout from '../../v2/layouts/blog';

interface BlogPostsQuery {
  data: {
    blogPostHero: IBlogPosts;
  };
  location: WindowLocation;
}

const BaseCategory = BLOG_CATEGORIES[0];

const BaseBlog = ({ data, location }: BlogPostsQuery) => {
  const [activeCategory, setActiveCategory] = useState(BaseCategory);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [keyword, setKeyword] = useState<string>('');
  const firstBlogPost = data.blogPostHero.edges[0].node;

  const [{ data: categoriesData, paginationData, isLoading }, dispatch] =
    useReducer(blogReducer, {
      isLoading: false,
      data: {},
      paginationData: {},
    });

  const isPaginationAvailable = Boolean(
    paginationData[activeCategory.value]?.limit,
  );

  const canShowBlogCards =
    categoriesData &&
    activeCategory &&
    categoriesData[activeCategory.value] &&
    categoriesData[activeCategory.value][pageNumber]?.length;

  const { totalPagesNumber } = pageNumberController({
    isPaginationAvailable,
    limit: BLOG_INDEX_PAGE_LIMIT,
    total: paginationData[activeCategory.value]?.total,
    currentPage: pageNumber,
  });

  function checkRequestCondition() {
    if (!isPaginationAvailable) return true;
    if (
      categoriesData[activeCategory.value] &&
      categoriesData[activeCategory.value][pageNumber] &&
      categoriesData[activeCategory.value][pageNumber]?.length
    )
      return false;
    return pageNumber > 0 && pageNumber <= totalPagesNumber!;
  }

  const handleActiveCategory = (item: {
    value: string;
    key: string;
    color: string;
    fromExternalLink?: boolean;
  }) => {
    setActiveCategory(item);
    setPageNumber(1);
    if (location.search.length && !item.fromExternalLink) {
      const params = new URLSearchParams(location.search);
      const fromQuery = params.get('from')?.toLowerCase();
      if (fromQuery === 'tips' || fromQuery === 'insurance') {
        void navigate('/blog');
      }
    }
  };

  const updatePaginationQueryParam = (newPageNumber: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(newPageNumber));
    window.history.pushState({}, '', url.toString());
  };

  useEffect(() => {
    if (checkRequestCondition()) {
      updatePaginationQueryParam(pageNumber);
      blogPostsQuery({
        pageNumber,
        activeCategory,
        categoriesData,
        paginationData,
        dispatch,
        keyword,
      });
    }
  }, [activeCategory, pageNumber, keyword]);

  useEffect(() => {
    if (location?.search?.length) {
      const params = new URLSearchParams(location.search);
      const fromQuery = params.get('from')?.toLowerCase();
      if (fromQuery === 'tips') {
        handleActiveCategory({
          value: 'Exterior Contractors',
          key: 'exterior-contractors',
          color: 'brandOrange',
          fromExternalLink: true,
        });
      }
      if (fromQuery === 'insurance') {
        handleActiveCategory({
          value: 'Insurance',
          key: 'insurance',
          color: 'brandBrown',
          fromExternalLink: true,
        });
      }
    }
  }, [location]);

  return (
    <BlogLayout
      title="Blog"
      description="We share the latest industry trends, best practices, HOVER product and company news here."
      pageCategory={BaseCategory}
      activeCategory={activeCategory}
      firstBlogPost={firstBlogPost}
      isLoading={isLoading}
      onCategoryChange={handleActiveCategory}
      searchKeyword={keyword}
      setSearchKeyword={setKeyword}
      categoriesData={categoriesData}
      pageNumber={pageNumber}
      blogReducerDispatch={dispatch}
    >
      {canShowBlogCards && (
        <BlogPostList
          posts={categoriesData[activeCategory.value][pageNumber]}
          paginationData={paginationData}
          activeCategory={activeCategory}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
        />
      )}
    </BlogLayout>
  );
};

export default BaseBlog;

export const pageQuery = graphql`
  query BaseBlogQuery($category: String) {
    blogPostHero: allContentfulBlogPost(
      filter: { categories: { eq: $category } }
      sort: { fields: [publishDate], order: DESC }
      limit: 1
    ) {
      edges {
        node {
          title
          slug
          categories
          publishDate(formatString: "MM/DD/YYYY")
          heroImage {
            fluid(maxHeight: 585, resizingBehavior: SCALE) {
              ...GatsbyContentfulFluid
            }
          }
          description {
            description
          }
        }
      }
    }
  }
`;
